import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Sparkles, 
  Zap,
  Brain,
  Cpu,
  BarChart3,
  Columns,
  Lock,
  ArrowRight,
  Info
} from 'lucide-react';

// AI Model definitions with style characteristics
export const AI_MODELS = [
  { 
    id: 'gpt', 
    name: 'GPT', 
    icon: '🤖',
    description: 'Clean, structured, balanced',
    color: 'from-emerald-500 to-teal-500'
  },
  { 
    id: 'claude', 
    name: 'Claude', 
    icon: '🧠',
    description: 'Descriptive, natural tone',
    color: 'from-orange-500 to-amber-500'
  },
  { 
    id: 'gemini', 
    name: 'Gemini', 
    icon: '✨',
    description: 'Concise, technical, fast',
    color: 'from-blue-500 to-cyan-500'
  },
  { 
    id: 'llama', 
    name: 'Llama', 
    icon: '🦙',
    description: 'Straightforward, minimal',
    color: 'from-purple-500 to-violet-500'
  },
  { 
    id: 'mistral', 
    name: 'Mistral', 
    icon: '🌀',
    description: 'Efficient, compact',
    color: 'from-indigo-500 to-blue-500'
  },
  { 
    id: 'deepseek', 
    name: 'DeepSeek', 
    icon: '🔍',
    description: 'Analytical, detailed',
    color: 'from-slate-500 to-zinc-500'
  },
  { 
    id: 'perplexity', 
    name: 'Perplexity', 
    icon: '📚',
    description: 'Research-style output',
    color: 'from-cyan-500 to-sky-500'
  },
  { 
    id: 'grok', 
    name: 'Grok', 
    icon: '⚡',
    description: 'Casual, modern tone',
    color: 'from-red-500 to-rose-500'
  },
  { 
    id: 'cohere', 
    name: 'Cohere', 
    icon: '💎',
    description: 'Marketing-friendly, polished',
    color: 'from-pink-500 to-fuchsia-500'
  },
  { 
    id: 'other', 
    name: 'Other', 
    icon: '🔧',
    description: 'Balanced default style',
    color: 'from-gray-500 to-slate-500'
  },
];

// Model access by plan (for paid users)
// NOTE: During trial, ALL models are accessible - no locks!
const MODEL_ACCESS = {
  free: ['gpt'],  // Fallback only - trial users get ALL models
  starter: ['gpt'],
  growth: ['gpt', 'claude', 'gemini', 'mistral'],
  pro: ['gpt', 'claude', 'gemini', 'llama', 'mistral', 'deepseek', 'perplexity', 'grok', 'cohere', 'other']
};

// During trial, ALL models are accessible
const TRIAL_ACCESSIBLE_MODELS = ['gpt', 'claude', 'gemini', 'llama', 'mistral', 'deepseek', 'perplexity', 'grok', 'cohere', 'other'];

// Get upgrade plan for a model
const getUpgradePlanForModel = (currentPlan, modelId) => {
  const planOrder = ['free', 'starter', 'growth', 'pro'];
  const currentIdx = planOrder.indexOf(currentPlan);
  
  for (let i = currentIdx + 1; i < planOrder.length; i++) {
    const plan = planOrder[i];
    if (MODEL_ACCESS[plan]?.includes(modelId)) {
      return plan;
    }
  }
  return 'pro';
};

// Upgrade Modal Component - Updated for trial limit reached
const UpgradeModal = ({ isOpen, onClose, targetPlan, modelName, trialLimitReached = false, trialUsed = 0, trialLimit = 3 }) => {
  const navigate = useNavigate();
  
  const planDisplayNames = {
    starter: 'Starter',
    growth: 'Growth',
    pro: 'Pro'
  };
  
  const handleUpgrade = () => {
    onClose();
    navigate('/dashboard/settings', { state: { activeTab: 'billing' } });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {trialLimitReached ? (
              <>
                <Lock className="w-5 h-5 text-amber-500" />
                Trial Limit Reached
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 text-amber-500" />
                Unlock Better Prompt Quality
              </>
            )}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {trialLimitReached 
              ? "You've explored this model during your trial. Upgrade to continue using it with unlimited access."
              : "Access advanced models for higher-quality prompts and better results."
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center text-white">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">{modelName} {trialLimitReached ? 'trial limit reached' : 'is locked'}</p>
                <p className="text-sm text-muted-foreground">
                  {trialLimitReached 
                    ? `Used ${trialUsed}/${trialLimit} trial uses`
                    : `Available on ${planDisplayNames[targetPlan] || 'Pro'} plan`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>
            Maybe Later
          </Button>
          <Button onClick={handleUpgrade} className="gap-2" style={{ backgroundColor: '#4F7CFF' }}>
            Upgrade Plan
            <ArrowRight className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Horizontal pill selector variant with trial usage support
const ModelPillSelector = ({ 
  selectedModel, 
  onSelect, 
  compact = false, 
  userPlan = 'free',
  isTrial = false,
  modelStatus = [],
  onLockedClick 
}) => {
  const [expanded, setExpanded] = useState(false);
  
  // CRITICAL FIX: During trial, ALL models are accessible - NO LOCKS!
  const getModelInfo = (modelId) => {
    // Trial users get ALL models unlocked
    if (isTrial) {
      const statusInfo = modelStatus.find(m => m.id === modelId);
      // During trial, models are NEVER locked - only show usage remaining
      return { 
        accessible: true, // Always true during trial
        trial_remaining: statusInfo?.trial_remaining,
        trial_limit: statusInfo?.trial_limit,
        is_trial: true
      };
    }
    // For paid users, use MODEL_ACCESS
    const accessibleModels = MODEL_ACCESS[userPlan] || MODEL_ACCESS.free;
    return { accessible: accessibleModels.includes(modelId), is_trial: false };
  };
  
  // Show all models if expanded, or first 5 if compact
  const displayModels = (compact && !expanded) ? AI_MODELS.slice(0, 5) : AI_MODELS;
  const hiddenCount = AI_MODELS.length - 5;
  
  return (
    <div className="flex flex-wrap gap-2">
      {displayModels.map((model) => {
        const modelInfo = getModelInfo(model.id);
        // FIXED: During trial, NEVER show lock icons
        const isLocked = !isTrial && !modelInfo.accessible;
        const trialRemaining = modelInfo.trial_remaining;
        const trialLimit = modelInfo.trial_limit;
        
        return (
          <motion.button
            key={model.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.1 }}
            onClick={() => {
              if (isLocked) {
                onLockedClick?.(model, modelInfo);
              } else {
                onSelect(model.id);
              }
            }}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
              transition-all duration-150 border relative cursor-pointer
              ${isLocked 
                ? 'bg-muted/50 text-muted-foreground border-border/50 hover:bg-muted/70' 
                : selectedModel === model.id 
                  ? 'bg-foreground text-background border-foreground shadow-md' 
                  : 'bg-background hover:bg-muted border-border'
              }
            `}
            data-testid={`model-pill-${model.id}`}
            title={isLocked ? 'Upgrade to unlock' : model.description}
          >
            <span>{model.icon}</span>
            <span>{model.name}</span>
            {/* Only show lock for NON-trial locked models */}
            {isLocked && !isTrial && (
              <Lock className="w-3 h-3 ml-0.5 text-muted-foreground" />
            )}
            {/* Trial usage counter - show for trial users */}
            {isTrial && trialRemaining !== undefined && (
              <span className={`text-xs ml-1 px-1.5 py-0.5 rounded-full ${
                trialRemaining <= 1 
                  ? 'bg-amber-500/20 text-amber-600' 
                  : 'bg-green-500/20 text-green-600'
              }`}>
                {trialRemaining}/{trialLimit}
              </span>
            )}
          </motion.button>
        );
      })}
      {/* Expand/Collapse button - only show in compact mode */}
      {compact && hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-primary hover:text-primary/80 self-center px-2 py-1 rounded-full hover:bg-muted transition-colors cursor-pointer"
          data-testid="expand-models-btn"
        >
          {expanded ? 'Show less' : `+${hiddenCount} more`}
        </button>
      )}
    </div>
  );
};

// Dropdown selector variant with trial usage support
const ModelDropdownSelector = ({ 
  selectedModel, 
  onSelect, 
  userPlan = 'free',
  isTrial = false,
  modelStatus = [],
  onLockedClick 
}) => {
  // CRITICAL FIX: During trial, ALL models are accessible - NO LOCKS!
  const getModelInfo = (modelId) => {
    if (isTrial) {
      const statusInfo = modelStatus.find(m => m.id === modelId);
      return { 
        accessible: true, // Always true during trial
        trial_remaining: statusInfo?.trial_remaining,
        trial_limit: statusInfo?.trial_limit,
        is_trial: true
      };
    }
    const accessibleModels = MODEL_ACCESS[userPlan] || MODEL_ACCESS.free;
    return { accessible: accessibleModels.includes(modelId), is_trial: false };
  };
  
  const selected = AI_MODELS.find(m => m.id === selectedModel) || AI_MODELS[0];
  const selectedInfo = getModelInfo(selectedModel);
  
  return (
    <Select 
      value={selectedModel} 
      onValueChange={(value) => {
        const model = AI_MODELS.find(m => m.id === value);
        const modelInfo = getModelInfo(value);
        // During trial, never block - just select
        if (!isTrial && !modelInfo.accessible) {
          onLockedClick?.(model, modelInfo);
        } else {
          onSelect(value);
        }
      }}
    >
      <SelectTrigger className="w-[220px]" data-testid="model-dropdown">
        <SelectValue>
          <div className="flex items-center gap-2">
            <span>{selected.icon}</span>
            <span>{selected.name}</span>
            {isTrial && selectedInfo.trial_remaining !== undefined && (
              <span className="text-xs text-muted-foreground">
                ({selectedInfo.trial_remaining} left)
              </span>
            )}
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {AI_MODELS.map((model) => {
          const modelInfo = getModelInfo(model.id);
          // FIXED: During trial, NEVER show as locked
          const isLocked = !isTrial && !modelInfo.accessible;
          
          return (
            <SelectItem 
              key={model.id} 
              value={model.id}
              data-testid={`model-option-${model.id}`}
              className={isLocked ? 'opacity-60' : ''}
            >
              <div className="flex items-center gap-2 w-full">
                <span>{model.icon}</span>
                <div className="flex-1">
                  <span className={`font-medium`}>{model.name}</span>
                  {isTrial && modelInfo.trial_remaining !== undefined && (
                    <span className="text-xs text-green-600 ml-2">
                      {modelInfo.trial_remaining}/{modelInfo.trial_limit}
                    </span>
                  )}
                </div>
                {isLocked && !isTrial && <Lock className="w-3 h-3 text-muted-foreground ml-2" />}
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

// Main AI Model Selector Component
const AIModelSelector = ({ 
  selectedModel = 'gpt', 
  onModelChange, 
  variant = 'pills', // 'pills' | 'dropdown' | 'cards'
  showCompare = false,
  compareEnabled = false,
  onCompareToggle,
  compact = false,
  className = ''
}) => {
  const { user, api } = useAuth();
  const [upgradeModal, setUpgradeModal] = useState({ 
    isOpen: false, 
    model: null, 
    targetPlan: null,
    trialLimitReached: false,
    trialUsed: 0,
    trialLimit: 3
  });
  const [modelStatus, setModelStatus] = useState([]);
  const [isTrial, setIsTrial] = useState(false);
  
  const userPlan = user?.plan || 'free';
  const selected = AI_MODELS.find(m => m.id === selectedModel) || AI_MODELS[0];
  
  // Fetch model access status (including trial usage)
  useEffect(() => {
    const fetchModelAccess = async () => {
      try {
        const response = await api.get('/users/model-access');
        setModelStatus(response.data.model_status || []);
        setIsTrial(response.data.is_trial || false);
      } catch (error) {
        console.error('Failed to fetch model access:', error);
      }
    };
    
    if (user) {
      fetchModelAccess();
    }
  }, [user, api]);
  
  // Ensure selected model is accessible
  useEffect(() => {
    if (modelStatus.length > 0) {
      const currentModelStatus = modelStatus.find(m => m.id === selectedModel);
      if (currentModelStatus && !currentModelStatus.accessible) {
        // Find first accessible model
        const firstAccessible = modelStatus.find(m => m.accessible);
        if (firstAccessible) {
          onModelChange?.(firstAccessible.id);
        }
      }
    }
  }, [modelStatus, selectedModel, onModelChange]);
  
  const handleLockedClick = useCallback((model, modelInfo = {}) => {
    if (isTrial && modelInfo.locked_reason === 'trial_limit_reached') {
      setUpgradeModal({ 
        isOpen: true, 
        model, 
        targetPlan: 'starter',
        trialLimitReached: true,
        trialUsed: modelInfo.trial_used || 0,
        trialLimit: modelInfo.trial_limit || 3
      });
    } else {
      const targetPlan = getUpgradePlanForModel(userPlan, model.id);
      setUpgradeModal({ 
        isOpen: true, 
        model, 
        targetPlan,
        trialLimitReached: false,
        trialUsed: 0,
        trialLimit: 3
      });
    }
  }, [userPlan, isTrial]);

  // Get current model's trial info
  const currentModelInfo = modelStatus.find(m => m.id === selectedModel);

  return (
    <div className={`space-y-3 ${className}`} data-testid="ai-model-selector">
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={upgradeModal.isOpen}
        onClose={() => setUpgradeModal({ isOpen: false, model: null, targetPlan: null, trialLimitReached: false, trialUsed: 0, trialLimit: 3 })}
        targetPlan={upgradeModal.targetPlan}
        modelName={upgradeModal.model?.name}
        trialLimitReached={upgradeModal.trialLimitReached}
        trialUsed={upgradeModal.trialUsed}
        trialLimit={upgradeModal.trialLimit}
      />
      
      {/* Header with trial badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-medium">AI Model</Label>
          {isTrial && (
            <span className="text-xs px-2 py-0.5 bg-green-500/10 text-green-600 rounded-full flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Trial: All models unlocked
            </span>
          )}
        </div>
        
        {/* Compare toggle */}
        {showCompare && (
          <div className="flex items-center gap-2">
            <Columns className="w-4 h-4 text-muted-foreground" />
            <Label htmlFor="compare-toggle" className="text-xs text-muted-foreground cursor-pointer">
              Compare
            </Label>
            <Switch
              id="compare-toggle"
              checked={compareEnabled}
              onCheckedChange={onCompareToggle}
              data-testid="compare-toggle"
            />
          </div>
        )}
      </div>

      {/* Selector */}
      {variant === 'pills' && (
        <ModelPillSelector 
          selectedModel={selectedModel} 
          onSelect={onModelChange}
          compact={compact}
          userPlan={userPlan}
          isTrial={isTrial}
          modelStatus={modelStatus}
          onLockedClick={handleLockedClick}
        />
      )}
      
      {variant === 'dropdown' && (
        <ModelDropdownSelector 
          selectedModel={selectedModel} 
          onSelect={onModelChange}
          userPlan={userPlan}
          isTrial={isTrial}
          modelStatus={modelStatus}
          onLockedClick={handleLockedClick}
        />
      )}

      {/* Selected model info + Quality hint */}
      <div className="space-y-1">
        {!compact && (
          <motion.div
            key={selectedModel}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-xs text-muted-foreground"
          >
            <Zap className="w-3 h-3" />
            <span>{selected.name}: {selected.description}</span>
            {isTrial && currentModelInfo?.trial_remaining !== undefined && (
              <span className={`ml-2 px-1.5 py-0.5 rounded ${
                currentModelInfo.trial_remaining <= 1 
                  ? 'bg-amber-500/20 text-amber-600' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                {currentModelInfo.trial_remaining} uses remaining
              </span>
            )}
          </motion.div>
        )}
        <p className="text-xs text-muted-foreground/70 italic">
          {isTrial 
            ? "Try all models during your trial – upgrade for unlimited access"
            : "Better models = better prompt quality"
          }
        </p>
      </div>
    </div>
  );
};

// Model style transformation functions for prompt generation
export const getModelStylePrompt = (modelId, basePrompt, context = 'enhance') => {
  const styleGuides = {
    gpt: {
      prefix: '',
      suffix: '',
      style: 'clean, structured, and balanced',
      formatting: 'well-organized with clear sections'
    },
    claude: {
      prefix: '',
      suffix: '',
      style: 'descriptive, natural, and conversational',
      formatting: 'flowing narrative with rich details'
    },
    gemini: {
      prefix: '',
      suffix: '',
      style: 'concise, technical, and efficient',
      formatting: 'compact with key technical terms'
    },
    llama: {
      prefix: '',
      suffix: '',
      style: 'straightforward and minimal',
      formatting: 'direct with minimal embellishment'
    },
    mistral: {
      prefix: '',
      suffix: '',
      style: 'efficient and optimized',
      formatting: 'compact with precise wording'
    },
    deepseek: {
      prefix: '',
      suffix: '',
      style: 'analytical and detailed',
      formatting: 'technical with thorough analysis'
    },
    perplexity: {
      prefix: '',
      suffix: '',
      style: 'informational and research-oriented',
      formatting: 'structured like research findings'
    },
    grok: {
      prefix: '',
      suffix: '',
      style: 'casual and modern',
      formatting: 'conversational with contemporary language'
    },
    cohere: {
      prefix: '',
      suffix: '',
      style: 'marketing-friendly and polished',
      formatting: 'persuasive with compelling language'
    },
    other: {
      prefix: '',
      suffix: '',
      style: 'balanced and versatile',
      formatting: 'standard professional output'
    }
  };

  const guide = styleGuides[modelId] || styleGuides.gpt;
  
  return {
    styleGuide: guide,
    enhancedPrompt: `Generate output in a ${guide.style} manner. The formatting should be ${guide.formatting}. ${basePrompt}`
  };
};

// Simulate different model outputs for comparison
export const simulateModelOutput = (modelId, baseContent) => {
  const transformations = {
    gpt: (content) => {
      // Clean, structured output
      return content;
    },
    claude: (content) => {
      // More descriptive, natural
      const additions = [
        'with careful attention to detail',
        'capturing the essence of',
        'thoughtfully composed',
        'with natural elegance'
      ];
      const addition = additions[Math.floor(Math.random() * additions.length)];
      return `${content}, ${addition}`;
    },
    gemini: (content) => {
      // Concise, technical
      return content.split(',').slice(0, 3).join(',').trim();
    },
    llama: (content) => {
      // Straightforward, minimal
      return content.replace(/,\s*(featuring|with|showing)/gi, '').trim();
    },
    mistral: (content) => {
      // Efficient, compact
      const words = content.split(' ').filter(w => w.length > 3);
      return words.slice(0, Math.ceil(words.length * 0.8)).join(' ');
    },
    deepseek: (content) => {
      // Analytical, detailed
      return `[Analysis] ${content}. [Technical details: high resolution, optimized composition, professional grade]`;
    },
    perplexity: (content) => {
      // Research-style
      return `Based on visual analysis: ${content}. This composition demonstrates typical characteristics of professional product imagery.`;
    },
    grok: (content) => {
      // Casual, modern
      return content.replace(/professional/gi, 'pro').replace(/beautiful/gi, 'stunning');
    },
    cohere: (content) => {
      // Marketing-friendly
      return `✨ ${content} - Perfect for captivating your audience and driving engagement`;
    },
    other: (content) => content
  };

  const transform = transformations[modelId] || transformations.gpt;
  return transform(baseContent);
};

export default AIModelSelector;
